import { Link, LinkProps } from "@tanstack/react-router"
import { Card, CardDescription, CardHeader, CardTitle } from "design-system/molecules/Card"
import { LucideIcon } from "lucide-react"
import { ComponentType, SVGProps } from "react"
import { useTranslation } from "react-i18next"

type BaseItem = {
  title: string
  icon?: LucideIcon | ComponentType<SVGProps<SVGSVGElement>>
  logo?: string
  params?: Record<string, string | number>
}

type InternalItem = BaseItem & {
  url: LinkProps["to"]
  externalUrl?: never
}

type ExternalItem = BaseItem & {
  url?: LinkProps["to"]
  externalUrl: string
}

type BackofficeSectionItem = InternalItem | ExternalItem

type Props = {
  titleKey?: string
  descKey?: string
  items: BackofficeSectionItem[]
}

export const BackofficeSection = ({ titleKey, descKey, items }: Props) => {
  const { t } = useTranslation()
  return (
    <section className="flex flex-col gap-6">
      {(titleKey || descKey) && (
        <div className="flex flex-col gap-2">
          {titleKey && <CardTitle>{t(titleKey)}</CardTitle>}
          {descKey && <CardDescription>{t(descKey)}</CardDescription>}
        </div>
      )}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 xl:grid-cols-6 w-full gap-3">
        {items.map((card) => (
          <Link
            key={card.title}
            to={card.url}
            params={card.params as never}
            className="w-full"
            onClick={(ev) => {
              if (card.externalUrl) {
                ev.preventDefault()
                window.open(card.externalUrl)
              }
            }}
          >
            <Card className="hover:bg-muted">
              <CardHeader className="flex items-center gap-2">
                <CardTitle>
                  {card.logo ? (
                    <img src={card.logo} alt={card.title} className="size-13 object-contain" />
                  ) : (
                    card.icon && <card.icon className="size-12" />
                  )}
                </CardTitle>
                <CardDescription className="text-sm line-clamp-1" title={card.title}>
                  {card.title}
                </CardDescription>
              </CardHeader>
            </Card>
          </Link>
        ))}
      </div>
    </section>
  )
}
